import React, { Component } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import includes from 'lodash/includes';
import remove from 'lodash/remove';
import { connect } from 'react-redux';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Warning from '@material-ui/icons/Warning';
import withStyles from '@material-ui/core/styles/withStyles';
import { yellow, amber } from '@material-ui/core/colors';
import { SketchOrderStatus } from '../../lib/sketchOrderStatuses';
import featureFlags from '../../lib/FeatureFlags';
import {
    createNotification,
    updateNotification,
    removeNotification,
} from '../../actions/sketchOrderNotificationActions';
import { getNotifications } from '../../actions/sketchOrderNotificationsActions';
import { getNotificationTemplates } from '../../actions/notificationTemplatesActions';
import { getProject } from '../../actions/projectActions';
import { getProjectDrawingSummary } from '../../actions/projectDrawingSummaryActions';
import {
    getSketchOrder,
    updateSketchOrder,
    deleteSketchOrderProject,
} from '../../actions/sketchOrderActions';
import { getSketchOrderTiers } from '../../actions/sketchOrderTiersActions';
import { getUserProfiles } from '../../actions/userProfilesActions';
import Alert from '../../components/Alert';
import RoofSnapConfirmationDialog from '../../components/RoofSnapConfirmationDialog';
import RoofSnapSnackBar from '../../components/RoofSnapSnackBar';
import SetSketchTechDialog from '../SetSketchTechDialog/SetSketchTechDialog';
import SketchOrderCard from '../../components/SketchOrderCard';
import SketchOrderNotificationsDrawer from '../../components/SketchOrderNotificationsDrawer';
import SketchOrderDeliveryOptions from '../../components/SketchOrderDeliveryOptions';
import TransitionSketchOrderButton from '../../components/TransitionSketchOrderButton';

const styles = (theme) => ({
    root: {
        position: 'relative',
    },
    warning: {
        backgroundColor: yellow[100],
        border: `solid 3px ${amber[500]}`,
        marginBottom: theme.spacing(1),
    },
    warningIcon: {
        backgroundColor: yellow[100],
        color: amber[500],
        fontSize: 42,
    },
    warningTitle: {
        color: amber[500],
        fontSize: 24,
    },
    warningText: {
        color: amber[500],
    },
});

export class SketchOrder extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sketchOrder: { deliveryOptions: [] },
            project: {},
            newSketchOrderStatus: '',
            newLocation: null,
            newSketchTechUserId: null,
            message: '',
            messageType: '',
            snackBar: false,
            confirmationDialogOpen: false,
            dialogTitle: '',
            reportTypeChanged: false,
            setSketchTechDialogOpen: false,
            drawerMode: 'list',
            selectedTemplate: null,
            notification: null,
            deleteProjectDialogOpen: false,
            alertMessage: '',
            alertOpen: false,
        };

        this.updateSketchOrder = this.updateSketchOrder.bind(this);
    }

    async componentDidMount() {
        await Promise.all([
            this.getSketchOrderInformation(),
            this.props.dispatch(getSketchOrderTiers()),
        ]);
        await this.props.dispatch(getNotifications(this.props.match.params.id));
        this.getNotificationTemplates();
    }

    componentDidUpdate(prevProps) {
        const { totalPages, currentPage, isFetching } = this.props.templates;
        if (prevProps.templates && !isFetching && currentPage !== totalPages) {
            this.getNotificationTemplates(currentPage + 1);
        }
    }

    sketchOrder = () =>
        this.props.sketchOrders[this.props.match.params.id] || {};

    getNotificationTemplates = (page = 1) => {
        this.props.dispatch(getNotificationTemplates(page));
    };

    getSketchOrderInformation = async () => {
        // try to pull the sketch order from the data store, if we have it
        let sketchOrder;
        if (this.sketchOrder().id) {
            sketchOrder = this.sketchOrder();
        } else {
            const result = await this.props.dispatch(
                getSketchOrder(this.props.match.params.id),
            );
            if (!result.response) return;

            sketchOrder = result.response;
        }

        this.setState({
            sketchOrder,
            newLocation: sketchOrder.newLocation,
        });

        // If the user is a sketch os admin or a roofsnap admin, go get the user profiles
        if (
            (this.props.currentUserRoles.isAdmin ||
                this.props.currentUserRoles.isSketchOsAdmin) &&
            sketchOrder.organization
        ) {
            await this.props.dispatch(
                getUserProfiles(1, null, sketchOrder.organization.id),
            );
        }

        await this.props.dispatch(
            getProjectDrawingSummary(sketchOrder.projectId),
        );
        await this.getProject(sketchOrder.projectId);
    };

    getProject = async (projectId) => {
        if (!projectId) {
            this.setState({
                project: {
                    isFetching: false,
                },
            });
            return;
        }

        const result = await this.props.dispatch(getProject(projectId));
        if (!result.response) return;

        this.setState({
            project: result.response,
        });
    };

    handleDeleteProjectClick = () => {
        this.setState({
            deleteProjectDialogOpen: true,
        });
    };

    deleteProject = async () => {
        const id = this.sketchOrder().id;
        const result = await this.props.dispatch(deleteSketchOrderProject(id));
        if (result.response && result.response.success) {
            this.setState({
                project: {
                    isFetching: false,
                },
                message: 'Project deleted',
                messageType: 'success',
                snackBar: true,
            });
        } else {
            this.setState({
                project: {
                    isFetching: false,
                },
                message: 'Project deletion failed',
                messageType: 'error',
                snackBar: true,
            });
        }
        await this.getSketchOrderInformation();
    };

    buildSketchOrder = () => {
        const { sketchOrder, newSketchOrderStatus } = this.state;
        const updatedSketchOrder = {
            projectName: sketchOrder.projectName,
            notes: sketchOrder.notes,
            sketchOrderStatus:
                newSketchOrderStatus || sketchOrder.sketchOrderStatus,
            address: sketchOrder.address,
            city: sketchOrder.city,
            region: sketchOrder.region,
            postCode: sketchOrder.postCode,
            country: sketchOrder.country,
            location: sketchOrder.location,
            sketchReportType: sketchOrder.sketchReportType,
            sketchOrderTierId: sketchOrder.sketchOrderTierId,
            cost: sketchOrder.cost,
            internalNotes: sketchOrder.internalNotes,
            sketchTechUserId:
                this.state.newSketchTechUserId ||
                this.sketchOrder().sketchTechUserId,
            deliveryOptions: sketchOrder.deliveryOptions,
            freeOrder: sketchOrder.freeOrder,
            rushed: sketchOrder.rushed,
            rushCost: sketchOrder.rushCost,
            dueDate: sketchOrder.dueDate,
        };

        if (sketchOrder.owner) {
            updatedSketchOrder.ownerUserId = sketchOrder.owner.userId;
        }

        return updatedSketchOrder;
    };

    async updateSketchOrder() {
        this.setState({
            alertOpen: false,
        });
        const sketchOrderToUpdate = this.buildSketchOrder();
        const result = await this.props.dispatch(
            updateSketchOrder(
                this.state.sketchOrder.id,
                sketchOrderToUpdate,
                this.sketchOrder().version,
            ),
        );
        if (result.response) {
            this.setState({
                sketchOrder: result.response,
                message: 'Order updated',
                messageType: 'success',
                snackBar: true,
                reportTypeChanged: false,
            });

            // Go get the updated sketch order's project
            await this.getProject(result.response.projectId);
        } else {
            this.setState({
                sketchOrder: this.sketchOrder(),
                alertOpen: true,
                alertMessage: 'Unable to update order',
            });
        }
    }

    updateSketchOrderRequest = async (status) => {
        this.setState(
            {
                newSketchOrderStatus: status,
            },
            async () => {
                // If we are moving from Exception to InProgress, set the Sketch Tech
                if (!this.state.newSketchTechUserId) {
                    if (
                        this.sketchOrder().sketchOrderStatus ===
                            SketchOrderStatus.Exception &&
                        this.state.newSketchOrderStatus ===
                            SketchOrderStatus.InProgress
                    ) {
                        this.setState({ setSketchTechDialogOpen: true });
                        return;
                    }
                }

                if (!this.state.confirmationDialogOpen) {
                    this.openConfirmationDialog();
                    return;
                }

                await this.updateSketchOrder();
            },
        );
    };

    searchOrganizationsUsers = async (searchValue) => {
        await this.props.dispatch(
            getUserProfiles(1, searchValue, this.sketchOrder().organization.id),
        );
    };

    openConfirmationDialog = () => {
        this.setState({
            dialogTitle: `Are you sure you want to move this order to ${this.state.newSketchOrderStatus}?`,
            confirmationDialogOpen: true,
        });
    };

    handleChange = (event) => {
        const sketchOrder = Object.assign({}, this.state.sketchOrder);
        sketchOrder[event.target.name] = event.target.value;
        this.setState({ sketchOrder });
    };

    handleSnackBarClose = () => {
        this.setState({ snackBar: false });
    };

    resetNewSketchTech = () => {
        const sketchOrder = Object.assign({}, this.state.sketchOrder);
        sketchOrder.sketchTech = this.sketchOrder().sketchTech;

        this.setState({
            newSketchTechUserId: null,
            sketchOrder,
        });
    };

    handleConfirmationDialogClose = () => {
        this.setState({
            confirmationDialogOpen: false,
        });
        this.resetNewSketchTech();
    };

    handleSetSketchDialogClose = () => {
        this.setState({
            setSketchTechDialogOpen: false,
        });
        this.resetNewSketchTech();
    };

    handleDeleteProjectDialogClose = () => {
        this.setState({
            deleteProjectDialogOpen: false,
        });
    };

    handleOwnerChange = (object) => {
        const sketchOrder = Object.assign({}, this.state.sketchOrder);

        sketchOrder.owner = {
            userId: object.value,
            userName: object.label,
        };
        this.setState({ sketchOrder });
    };

    handleSketchTechChange = (object) => {
        const sketchOrder = Object.assign({}, this.state.sketchOrder);

        sketchOrder.sketchTech = {
            userId: object.value,
            userName: object.label,
        };
        this.setState({ sketchOrder });
    };

    setSketchTech = async () => {
        this.setState({
            setSketchTechDialogOpen: false,
            newSketchTechUserId: this.state.sketchOrder.sketchTech.userId,
        });
        await this.updateSketchOrderRequest(this.state.newSketchOrderStatus);
    };

    handleLocationChange = (event) => {
        const newLocation = {
            coordinates: [event.latLng.lng(), event.latLng.lat()],
            type: 'Point',
        };
        const sketchOrder = Object.assign({}, this.state.sketchOrder);

        if (
            sketchOrder.sketchOrderGroup &&
            sketchOrder.sketchOrderGroup.mapMarkerPoints.length
        ) {
            const groupedMarker =
                sketchOrder.sketchOrderGroup.mapMarkerPoints.find((marker) => {
                    return (
                        marker.point.coordinates[0] ===
                            sketchOrder.location.coordinates[0] &&
                        marker.point.coordinates[1] ===
                            sketchOrder.location.coordinates[1]
                    );
                });

            groupedMarker.point = newLocation;
        }

        sketchOrder.location = newLocation;

        this.setState({ sketchOrder });
    };

    handleDeliveryOptionsChange = (deliveryOption, removeOption) => {
        const sketchOrder = Object.assign({}, this.state.sketchOrder);
        if (removeOption) {
            sketchOrder.deliveryOptions = remove(
                sketchOrder.deliveryOptions,
                deliveryOption,
            );
        } else {
            sketchOrder.deliveryOptions.push(deliveryOption);
        }
        this.setState({ sketchOrder });
    };

    handleOnFreeOrderChange = (event) => {
        const sketchOrder = Object.assign({}, this.state.sketchOrder);
        sketchOrder.freeOrder = event.target.checked;
        this.setState({ sketchOrder });
    };

    handleAddNotificationButtonClick = (e, id) => {
        const notification = find(this.props.notifications, ['id', id]);
        this.setState({
            drawerMode: 'new',
            notification,
        });
    };
    handleNotificationBackButtonClick = async () => {
        if (!this.state.notification) {
            this.setState({
                drawerMode: 'list',
            });
            return;
        }

        const { id, notificationTemplateId, customNotes, version } =
            this.state.notification;

        // New notification, so create!
        if (!id) {
            await this.props.dispatch(
                createNotification(this.props.match.params.id, {
                    createdByUserId: this.props.currentUser.userId,
                    notificationTemplateId,
                    customNotes,
                }),
            );
        } else {
            await this.props.dispatch(
                updateNotification(
                    this.props.match.params.id,
                    id,
                    {
                        notificationTemplateId,
                        customNotes,
                    },
                    version,
                ),
            );
        }

        this.setState({
            drawerMode: 'list',
            notification: null,
        });
    };
    handleNotificationDeleteButtonClick = async () => {
        if (!this.state.notification) return;

        await this.props.dispatch(
            removeNotification(
                this.props.match.params.id,
                this.state.notification.id,
            ),
        );

        this.setState({
            drawerMode: 'list',
            notification: null,
        });
    };
    handleTemplateSelected = (id) => {
        this.setState({
            notification: Object.assign({}, this.state.notification, {
                notificationTemplateId: id,
            }),
        });
    };
    handleCustomNotesChange = (e) => {
        this.setState({
            notification: Object.assign({}, this.state.notification, {
                customNotes: e.target.value,
            }),
        });
    };

    handleReportTypeChange = (event) => {
        const sketchOrder = Object.assign({}, this.state.sketchOrder);
        sketchOrder[event.target.name] = event.target.value;
        this.setState({
            sketchOrder: sketchOrder,
            reportTypeChanged: !this.state.reportTypeChanged,
        });
    };

    getTransitionButtonStyle = () => {
        const { isAdmin, isSketchOsAdmin } = this.props.currentUserRoles;

        if (isAdmin || isSketchOsAdmin) {
            return {
                right: 320,
            };
        }

        return null;
    };

    handleDismissAlert = () => {
        this.setState({
            alertOpen: false,
        });
    };

    render() {
        const { classes } = this.props;
        const { isAdmin, isSketchOsAdmin } = this.props.currentUserRoles;

        const { editableProperties } = this.sketchOrder();

        const canEditAddress = () => {
            const canEditAddress = includes(editableProperties, 'Address');
            const canEditCity = includes(editableProperties, 'City');
            const canEditRegion = includes(editableProperties, 'Region');
            const canEditPostCode = includes(editableProperties, 'Postcode');
            const canEditCountry = includes(editableProperties, 'Country');

            return (
                canEditAddress &&
                canEditCity &&
                canEditRegion &&
                canEditPostCode &&
                canEditCountry
            );
        };

        const showReportType =
            this.sketchOrder().sketchOrderStatus !==
                SketchOrderStatus.Pending ||
            isAdmin ||
            isSketchOsAdmin;

        const transitionButtonStyle = this.getTransitionButtonStyle();

        const sketchTechIsAssignedToSketchOrder =
            this.sketchOrder().sketchTechUserId ===
            this.props.currentUser.userId;
        const isInProgress =
            this.sketchOrder().sketchOrderStatus ===
            SketchOrderStatus.InProgress;
        const isPending =
            this.sketchOrder().sketchOrderStatus === SketchOrderStatus.Pending;
        const authorizedToOpenDrawing =
            this.props.isAdmin ||
            (this.props.isSketchTech &&
                isInProgress &&
                sketchTechIsAssignedToSketchOrder);
        return (
            <div className={classes.root}>
                <Alert
                    variant='error'
                    message='Unable to update order'
                    open={this.state.alertOpen}
                    maxWidth={700}
                    onDismiss={this.handleDismissAlert}
                />
                <SketchOrderCard
                    isFetching={this.sketchOrder().isFetching}
                    sketchOrder={this.state.sketchOrder}
                    project={this.state.project}
                    showViewProjectButton={isAdmin || isSketchOsAdmin}
                    showProjectName={!isPending || isSketchOsAdmin || isAdmin}
                    showOrganizationName={isAdmin || isSketchOsAdmin}
                    showRequester={isAdmin || isSketchOsAdmin}
                    showOwner={isAdmin || isSketchOsAdmin}
                    showCost={isAdmin || isSketchOsAdmin}
                    showLinksToSupportDashboard={isAdmin}
                    showReportType={showReportType}
                    onUpdateClick={this.updateSketchOrder}
                    sketchOrderTiers={this.props.sketchOrderTiers}
                    onSketchOrderChange={this.handleChange}
                    canCompleteSketchOrder={isAdmin || isSketchOsAdmin}
                    canEditInternalNotes={includes(
                        editableProperties,
                        'InternalNotes',
                    )}
                    canEditAddress={canEditAddress()}
                    canEditLocation={includes(editableProperties, 'Location')}
                    canEditReportType={includes(
                        editableProperties,
                        'SketchReportType',
                    )}
                    canEditCost={includes(editableProperties, 'Cost')}
                    canEditTier={includes(
                        editableProperties,
                        'SketchOrderTierId',
                    )}
                    canEditProjectName={includes(
                        editableProperties,
                        'Projectname',
                    )}
                    canEditOwner={includes(editableProperties, 'OwnerUserId')}
                    canEditFreeOrder={includes(editableProperties, 'FreeOrder')}
                    onOwnerChange={this.handleOwnerChange}
                    organizationsUsers={this.props.userProfiles}
                    onSearchUsers={this.searchOrganizationsUsers}
                    onLocationChange={this.handleLocationChange}
                    selectedLocation={this.state.newLocation}
                    authorizedToOpenDrawing={authorizedToOpenDrawing}
                    currentUserRoles={this.props.currentUserRoles}
                    projectDrawingSummary={this.props.projectDrawingSummary}
                    onFreeOrderChange={this.handleOnFreeOrderChange}
                    reportTypeChanged={this.state.reportTypeChanged}
                    onReportTypeChange={this.handleReportTypeChange}
                    onDeleteProjectButtonClick={this.handleDeleteProjectClick}
                    currentUserName={this.props.currentUser.userName}
                />

                <RoofSnapSnackBar
                    open={this.state.snackBar}
                    message={this.state.message}
                    onClose={this.handleSnackBarClose}
                    variant={this.state.messageType}
                />

                <RoofSnapConfirmationDialog
                    open={this.state.confirmationDialogOpen}
                    title={this.state.dialogTitle}
                    onDismissClick={this.handleConfirmationDialogClose}
                    onConfirmClick={this.updateSketchOrder}
                    content={
                        <React.Fragment>
                            {this.state.sketchOrder.sketchTech &&
                                this.state.sketchOrder.sketchOrderStatus ===
                                    SketchOrderStatus.InProgress &&
                                this.state.sketchOrder.sketchTech.userId !==
                                    this.props.currentUser.userId && (
                                    <Card
                                        elevation={0}
                                        className={this.props.classes.warning}
                                    >
                                        <CardHeader
                                            title='Warning'
                                            subheader='You are not the sketch technician assigned to this order.'
                                            avatar={
                                                <Warning
                                                    className={
                                                        this.props.classes
                                                            .warningIcon
                                                    }
                                                />
                                            }
                                            titleTypographyProps={{
                                                className:
                                                    this.props.classes
                                                        .warningTitle,
                                            }}
                                            subheaderTypographyProps={{
                                                className:
                                                    this.props.classes
                                                        .warningText,
                                            }}
                                        />
                                    </Card>
                                )}
                            {this.state.sketchOrder.reviewer &&
                                this.state.sketchOrder.sketchOrderStatus ===
                                    SketchOrderStatus.InReview &&
                                this.state.sketchOrder.reviewer.userId !==
                                    this.props.currentUser.userId && (
                                    <Card
                                        elevation={0}
                                        className={this.props.classes.warning}
                                    >
                                        <CardHeader
                                            title='Warning'
                                            subheader='You are not the reviewer assigned to this order.'
                                            avatar={
                                                <Warning
                                                    className={
                                                        this.props.classes
                                                            .warningIcon
                                                    }
                                                />
                                            }
                                            titleTypographyProps={{
                                                className:
                                                    this.props.classes
                                                        .warningTitle,
                                            }}
                                            subheaderTypographyProps={{
                                                className:
                                                    this.props.classes
                                                        .warningText,
                                            }}
                                        />
                                    </Card>
                                )}
                            {this.state.sketchOrder.sketchOrderStatus ===
                                SketchOrderStatus.InReview &&
                                featureFlags.sketchOrderDeliveryOptions && (
                                    <SketchOrderDeliveryOptions
                                        sketchOrder={this.state.sketchOrder}
                                        onSketchOrderChange={
                                            this.handleDeliveryOptionsChange
                                        }
                                    />
                                )}
                        </React.Fragment>
                    }
                />
                <RoofSnapConfirmationDialog
                    open={this.state.deleteProjectDialogOpen}
                    title='Delete Project'
                    onDismissClick={this.handleDeleteProjectDialogClose}
                    onConfirmClick={this.deleteProject}
                    message='Do you really want to delete the project, snapped image and drawing for this order?'
                />

                {(isAdmin || isSketchOsAdmin) && (
                    <SetSketchTechDialog
                        open={this.state.setSketchTechDialogOpen}
                        sketchTech={this.state.sketchOrder.sketchTech}
                        onSketchTechChange={this.handleSketchTechChange}
                        onDismissClick={this.handleSetSketchDialogClose}
                        onSetSketchTechClick={this.setSketchTech}
                    />
                )}

                <TransitionSketchOrderButton
                    currentStatus={this.sketchOrder().sketchOrderStatus || ''}
                    allowedStatusTransitions={
                        this.sketchOrder().allowedTransitions
                    }
                    onClick={this.updateSketchOrderRequest}
                    disabled={this.sketchOrder().isFetching}
                    buttonStyle={transitionButtonStyle}
                />

                {(isAdmin || isSketchOsAdmin) && (
                    <SketchOrderNotificationsDrawer
                        onAddNotificationButtonClick={
                            this.handleAddNotificationButtonClick
                        }
                        notification={this.state.notification}
                        templates={this.props.templates.templates}
                        onBackButtonClick={
                            this.handleNotificationBackButtonClick
                        }
                        onDeleteButtonClick={
                            this.handleNotificationDeleteButtonClick
                        }
                        onTemplateChange={this.handleTemplateSelected}
                        onCustomNotesChange={this.handleCustomNotesChange}
                        notifications={this.props.notifications}
                        drawerMode={this.state.drawerMode}
                        sketchOrderGroup={this.sketchOrder().sketchOrderGroup}
                        sketchOrderId={this.sketchOrder().id}
                    />
                )}
            </div>
        );
    }
}

SketchOrder.propTypes = {
    dispatch: PropTypes.func.isRequired,
    isFetching: PropTypes.bool.isRequired,
    project: PropTypes.shape({
        id: PropTypes.string,
        hasDrawing: PropTypes.bool,
        isFetching: PropTypes.bool.isRequired,
    }).isRequired,
    sketchOrderTiers: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            sketchOrderTierDefinition: PropTypes.shape({
                minimumValue: PropTypes.number.isRequired,
                maximumValue: PropTypes.number.isRequired,
                sketchOrderTierDefinition: PropTypes.string.isRequired,
            }).isRequired,
        }),
    ).isRequired,
    currentUserRoles: PropTypes.shape({
        isAdmin: PropTypes.bool,
        isSketchOsAdmin: PropTypes.bool,
        isSketchTech: PropTypes.bool,
    }).isRequired,
    userProfiles: PropTypes.arrayOf(
        PropTypes.shape({
            userId: PropTypes.number,
            userName: PropTypes.string,
        }),
    ).isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            id: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    projectDrawingSummary: PropTypes.shape({
        numberOfFacets: PropTypes.number,
        actualSquares: PropTypes.number,
    }).isRequired,
    freeOrder: PropTypes.bool,
    templates: PropTypes.shape({
        templates: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number.isRequired,
                label: PropTypes.string.isRequired,
                description: PropTypes.string.isRequired,
                infoLink: PropTypes.string.isRequired,
                priorityLevel: PropTypes.string.isRequired,
                notificationType: PropTypes.string.isRequired,
                active: PropTypes.bool.isRequired,
            }).isRequired,
        ).isRequired,
        totalResults: PropTypes.number.isRequired,
        totalPages: PropTypes.number.isRequired,
        currentPage: PropTypes.number.isRequired,
    }).isRequired,
    currentUser: PropTypes.shape({
        userId: PropTypes.number.isRequired,
    }).isRequired,
};

const mapStateToProps = (state) => {
    const {
        currentUserRoles,
        sketchOrderTiers: sketchOrderTiersStore,
        userProfiles: userProfilesStore,
        project,
        currentUser,
        projectDrawingSummary,
        freeOrder,
        notificationTemplates: templates,
        sketchOrderNotifications,
        sketchOrders: sketchOrdersStore,
    } = state;
    const { sketchOrderTiers } = sketchOrderTiersStore;
    const { userProfiles } = userProfilesStore;
    const { sketchOrders } = sketchOrdersStore;

    const { isSketchTech } = currentUserRoles;
    const isAdmin =
        currentUserRoles.isAdmin || currentUserRoles.isSketchOsAdmin;

    const notifications = sketchOrderNotifications.notifications.map((n) => {
        return {
            ...n,
            isAcknowledged:
                n.notificationAcknowledgements &&
                n.notificationAcknowledgements.resourceList &&
                n.notificationAcknowledgements.resourceList.length > 0,
            template: n.notificationTemplate,
        };
    });

    return {
        currentUserRoles,
        sketchOrderTiers,
        userProfiles,
        project,
        isAdmin,
        isSketchTech,
        projectDrawingSummary,
        freeOrder,
        templates,
        currentUser,
        notifications,
        sketchOrders,
    };
};

export default connect(mapStateToProps)(withStyles(styles)(SketchOrder));
